import { useLayoutEffect, useRef, useState } from "react";
import "./index.css";
/**
 * @callback onClickHandler
 * @param {PointerEvent} e
 * @param {Object} context
 */
/**
 * @param {Object} props
 * @param {onClickHandler} props.onClickHandler - What to do when click on button
 * @param {boolean} props.isDisabled
 * @param {boolean} props.isSelected
 * @param {string} props.cardBgColor - background color for card eg 'primary-card' note not in var
 * @param {string} props.isNoHoverRequired - if no hover is required pass true else by default is false
 * @param {string} props.isNoCursorRequired - if no cursor is required pass true else by default is false
 * @param {string} props.minHeight - optional to pass min height of card
 * @param {string} [props.maxWidth] - optional to pass max width of card
 * @param {string} props.hoverHeight - pass hover part height for proper animation
 * @param {React.ReactNode} props.children
 * @param {Object} props.context - This object is passed as the second parameter to onClickHandler
 *
 * @returns {JSX.Element}
 */
const CardContainer = ({
  isDisabled,
  isSelected,
  onClickHandler,
  children,
  context,
  cardBgColor,
  isNoHoverRequired,
  isNoCursorRequired,
  minHeight,
  maxWidth,
  filterRef,
  hoverHeight = 0,
}) => {
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState("fit-content");
  const [openUpward, setOpenUpward] = useState(false);

  let cardBody, cardHover;

  if (!Array.isArray(children)) {
    cardBody = children;
    cardHover = null;
  } else if (children.length > 2) {
    throw new Error(
      "Card container cannot have any more than a <card-body> and a <card-hover>",
    );
  } else {
    [cardBody, cardHover] = children;
  }

  const resize = () => {
    const height = cardRef.current.getBoundingClientRect().height;
    setCardHeight(`${height}px`);
  };

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const height = cardRef.current.getBoundingClientRect().height;
    setCardHeight(`${height}px`);

    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  const handleMouseEnter = () => {
    cardRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "end",
    });
    const container = filterRef?.current;
    const card = cardRef.current;

    if (!container || !card) return;

    const cardRect = card.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const willOverflowBottom =
      cardRect.bottom + Number(hoverHeight) > containerRect.bottom &&
      cardRect.top > containerRect.top + Number(hoverHeight);

    setOpenUpward(willOverflowBottom);
  };

  return (
    <div
      ref={cardRef}
      style={{ height: minHeight || cardHeight, maxWidth: maxWidth || "" }}
      className="standard-card-parent-container"
      onMouseEnter={handleMouseEnter}
    >
      <div
        style={{
          top: openUpward ? undefined : 0,
          bottom: openUpward ? 0 : undefined,
        }}
        onClick={(e) => {
          if (!isDisabled) onClickHandler(e, context);
        }}
        className={` ${isNoHoverRequired ? "" : isDisabled ? "" : isSelected ? "global-hover-black" : "global-hover-white"}  ${isDisabled ? "cursor-not-allowed  standard-card-container-disabled" : isNoCursorRequired ? "standard-card-container cursor-default" : "cursor-pointer standard-card-container"} ${isSelected ? "bg-positive-bg" : cardBgColor ? `bg-${cardBgColor}` : "bg-primary-card"}`}
      >
        <div
          className={`${cardHover ? "standard-card-div" : "standard-card-div-inactive"}`}
        >
          {cardBody}
        </div>
        <div className={cardHover ? `standard-card-hover-div` : ""}>
          {cardHover}
        </div>
      </div>
    </div>
  );
};

export default CardContainer;
